import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Loader2, ChevronDown, ShieldCheck, AlertTriangle } from "lucide-react";
import { API } from "../../lib/api";
import { fmtUSD } from "../../lib/format";

const PANEL_ORDER = ["gpt", "claude", "gemini"];

const fmtSigned = (n) => (n > 0 ? "+" : n < 0 ? "\u2212" : "") + fmtUSD(Math.abs(n));

const badgeStyle = (label) => {
  if (label === "High Agreement") return "bg-brand-emerald text-brand-cream";
  if (label === "Moderate Agreement") return "bg-brand-gold text-brand-dark";
  return "bg-red-600 text-white";
};

function PanelistCard({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-brand-hairline bg-brand-sand/60 p-4" data-testid={`panelist-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-brand-stone">{p.label}</p>
          <p className="text-[0.65rem] font-mono text-brand-stone/70">{p.model}</p>
        </div>
        {p.ok ? <ShieldCheck className="h-4 w-4 text-brand-emerald" /> : <AlertTriangle className="h-4 w-4 text-brand-gold" />}
      </div>
      {p.ok ? (
        <>
          <p className="mt-3 font-mono text-2xl font-bold text-brand-ink" data-testid={`panelist-value-${p.id}`}>{fmtUSD(p.valuation)}</p>
          {p.sqft_valuation_basis && <p className="text-xs text-brand-stone">Basis: {fmtUSD(p.sqft_valuation_basis)}/sqft</p>}
          <button onClick={() => setOpen((o) => !o)} data-testid={`panelist-toggle-${p.id}`}
            className="mt-3 flex items-center gap-1 text-xs font-semibold text-brand-emerald hover:underline">
            {open ? "Hide" : "Show"} reasoning <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="mt-3 space-y-2 text-xs text-brand-ink leading-relaxed" data-testid={`panelist-rationale-${p.id}`}>
              <p className="font-semibold text-brand-stone">Comps selected</p>
              <ul className="list-disc pl-4 space-y-0.5">{p.selected_comps.map((c, i) => <li key={i}>{c}</li>)}</ul>
              {p.adjustments?.length > 0 && (
                <div className="pt-1" data-testid={`panelist-adjustments-${p.id}`}>
                  <p className="font-semibold text-brand-stone">Adjustments (45% marginal size + lot)</p>
                  <div className="mt-1 overflow-x-auto">
                    <table className="w-full text-[0.7rem]">
                      <thead className="text-brand-stone/70">
                        <tr className="text-left">
                          <th className="pr-2 font-medium">Comp</th>
                          <th className="px-1 font-medium text-right">Sale</th>
                          <th className="px-1 font-medium text-right">Size</th>
                          <th className="px-1 font-medium text-right">Lot</th>
                          <th className="pl-1 font-medium text-right">Adjusted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.adjustments.map((a, i) => (
                          <tr key={i} className="border-t border-brand-hairline/60">
                            <td className="pr-2 py-1 text-brand-ink whitespace-nowrap">{(a.comp || "—").split(",")[0]}</td>
                            <td className="px-1 py-1 text-right font-mono">{a.sale_price != null ? fmtUSD(a.sale_price) : "—"}</td>
                            <td className="px-1 py-1 text-right font-mono">{a.size_adjustment != null ? fmtSigned(a.size_adjustment) : "—"}</td>
                            <td className="px-1 py-1 text-right font-mono">{a.lot_adjustment != null ? fmtSigned(a.lot_adjustment) : "—"}</td>
                            <td className="pl-1 py-1 text-right font-mono font-semibold text-brand-emerald">{a.adjusted_value != null ? fmtUSD(a.adjusted_value) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <p className="font-semibold text-brand-stone pt-1">Math</p>
              <p>{p.appraisal_rationale}</p>
            </div>
          )}
        </>
      ) : (
        <p className="mt-3 text-xs text-brand-stone" data-testid={`panelist-error-${p.id}`}>Excluded: {p.error}</p>
      )}
    </div>
  );
}

export default function AIPanelConsensus({ property, onResult }) {
  const [panelists, setPanelists] = useState([]);
  const [data, setData] = useState(null); // final consensus payload
  const [streaming, setStreaming] = useState(true);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setPanelists([]); setData(null); setStreaming(true); onResult?.(null);

    (async () => {
      try {
        const res = await fetch(`${API}/property/ai-panel/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ property }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error("stream unavailable");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl;
          while ((nl = buf.indexOf("\n")) >= 0) {
            const line = buf.slice(0, nl).trim();
            buf = buf.slice(nl + 1);
            if (!line) continue;
            let msg;
            try { msg = JSON.parse(line); } catch { continue; }
            if (!active) return;
            if (msg.type === "panelist") {
              setPanelists((prev) => [...prev.filter((x) => x.id !== msg.data.id), msg.data]);
            } else if (msg.type === "consensus") {
              setData(msg.data);
              if (Array.isArray(msg.data?.panelists)) setPanelists(msg.data.panelists);
              setStreaming(false);
              onResult?.(msg.data);
            }
          }
        }
        if (active) setStreaming(false);
      } catch {
        if (active) { setData({ ok: false, message: "AI panel unavailable right now." }); setStreaming(false); onResult?.({ ok: false }); }
      }
    })();

    return () => { active = false; controller.abort(); };
  }, [property]); // eslint-disable-line react-hooks/exhaustive-deps

  const ordered = [...panelists].sort((a, b) => PANEL_ORDER.indexOf(a.id) - PANEL_ORDER.indexOf(b.id));
  const okConsensus = data?.ok && data.consensus_value;
  const loading = streaming;

  return (
    <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="rounded-2xl border border-brand-hairline bg-white p-6 sm:p-8 shadow-[0_4px_20px_rgba(11,30,140,0.04)]"
      data-testid="ai-panel-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-emeraldLight"><Users className="h-4 w-4 text-brand-emerald" /></span>
        <span className="eyebrow">AI Appraisal Panel</span>
      </div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">AI Panel Consensus Valuation</h2>

      {loading && panelists.length === 0 && (
        <div className="mt-5 flex items-center gap-3 text-brand-stone" data-testid="ai-panel-loading">
          <Loader2 className="h-4 w-4 animate-spin text-brand-emerald" />
          <span className="text-sm">Three independent models are appraising your home from the comparable evidence…</span>
        </div>
      )}

      {/* Progressive reveal: cards appear the moment each model returns, before reconciliation */}
      {loading && panelists.length > 0 && (
        <div className="mt-6" data-testid="ai-panel-streaming">
          <div className="flex items-center gap-2 text-brand-stone" data-testid="ai-panel-progress">
            <Loader2 className="h-4 w-4 animate-spin text-brand-emerald" />
            <span className="text-sm">{panelists.length} of 3 models complete · reconciling consensus…</span>
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {ordered.map((p) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <PanelistCard p={p} />
              </motion.div>
            ))}
            {Array.from({ length: Math.max(0, 3 - panelists.length) }).map((_, i) => (
              <div key={`skeleton-${i}`} className="rounded-xl border border-dashed border-brand-hairline bg-brand-sand/30 p-4 animate-pulse" data-testid="ai-panel-skeleton">
                <div className="h-3 w-24 rounded bg-brand-hairline" />
                <div className="mt-4 h-7 w-32 rounded bg-brand-hairline" />
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && data && !data.ok && (
        <p className="mt-5 text-sm text-brand-stone" data-testid="ai-panel-unavailable">{data.message}</p>
      )}

      {!loading && okConsensus && (
        <>
          <div className="mt-6 grid lg:grid-cols-[1.1fr_2fr] gap-6">
            <div className="rounded-xl bg-brand-emerald text-brand-cream p-6">
              <span className="text-xs uppercase tracking-[0.2em] text-brand-cream/60">Consensus (median of {data.valid_count})</span>
              <p className="mt-2 font-mono text-3xl sm:text-4xl font-bold text-brand-gold" data-testid="ai-panel-consensus-value">{fmtUSD(data.consensus_value)}</p>
              <p className="mt-1 text-xs text-brand-cream/60 font-mono">{fmtUSD(data.min)} – {fmtUSD(data.max)} · spread {data.spread_pct}%</p>
              <span className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${badgeStyle(data.agreement_label)}`} data-testid="ai-panel-agreement-badge">
                {data.agreement_label} · {data.agreement_score}%
              </span>
              {data.broker_review_recommended && (
                <p className="mt-3 text-xs text-brand-cream/80" data-testid="ai-panel-broker-review">Models diverged on comparable selection — a broker-reviewed valuation is recommended.</p>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {ordered.map((p) => <PanelistCard key={p.id} p={p} />)}
            </div>
          </div>
          <p className="mt-5 text-[0.7rem] uppercase tracking-wider text-brand-stone/70" data-testid="ai-panel-disclosure">
            Reconciled across 3 independent AI models grounded strictly on RentCast comps ({data.evidence_comp_count} within 1 mile) and public records · Reconciliation is deterministic · Not an official appraisal
          </p>
        </>
      )}
    </motion.section>
  );
}
